import React, { useState, useEffect } from 'react';
import { FaCoffee, FaArrowLeft, FaCheck, FaExclamationTriangle, FaWeight, FaClipboardList, FaPlus, FaGlassWhiskey } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy,
  onSnapshot,
  where 
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { sendRoasterNotification } from '../services/roasterNotificationService';

const BaristaBeansRequestPage = () => {
  const { currentUser } = useAuth();
  const [availableBeans, setAvailableBeans] = useState([]);
  const [selectedBean, setSelectedBean] = useState(null);
  const [requestQuantity, setRequestQuantity] = useState('');
  const [notes, setNotes] = useState('');
  
  // Custom bean request state
  const [customBeanName, setCustomBeanName] = useState('');
  const [customUnit, setCustomUnit] = useState('kg');
  const [customQuantity, setCustomQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentStep, setCurrentStep] = useState('select'); // 'select', 'request', 'custom', 'milk', 'success'
  
  // Milk request state
  const [milkType, setMilkType] = useState('');
  const [milkQuantity, setMilkQuantity] = useState('');
  const [milkUnit, setMilkUnit] = useState('liters');

  // Add barista-page class to body to disable header gradient
  useEffect(() => {
    document.body.classList.add('barista-page');
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    return () => {
      document.body.classList.remove('barista-page');
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
    };
  }, []);

  // Fetch available beans from roaster inventory
  useEffect(() => {
    const fetchAvailableBeans = async () => {
      try {
        const q = query(
          collection(db, 'roasterInventory'),
          orderBy('beanName', 'asc')
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const beans = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            };
          }).filter(bean => bean.active !== false); // Filter out inactive beans
          
          setAvailableBeans(beans);
        }, (error) => {
          console.error('Snapshot error:', error);
          setError('Failed to load available beans: ' + error.message);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error fetching available beans:', error);
        setError('Failed to load available beans');
      }
    };

    fetchAvailableBeans();
  }, []);

  const handleBeanSelect = (bean) => {
    setSelectedBean(bean);
    setCurrentStep('request');
  };

  const handleBackToSelection = () => {
    setSelectedBean(null);
    setCurrentStep('select');
    setRequestQuantity('');
    setNotes('');
    setError(null);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const quantity = parseInt(requestQuantity);
      
      if (quantity <= 0) {
        setError('Please enter a valid quantity');
        setLoading(false);
        return;
      }

      // Allow requesting more than available quantity (beans may be unpacked)

      const requestData = {
        beanId: selectedBean.id,
        beanName: selectedBean.beanName,
        unit: selectedBean.unit,
        unitSize: selectedBean.unitSize,
        requestedQuantity: quantity,
        availableQuantity: selectedBean.quantity,
        notes: notes.trim() || '',
        status: 'pending',
        requestedBy: currentUser.uid,
        requestedByName: currentUser.displayName || currentUser.email,
        requestedByRole: currentUser.role || 'Staff',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'beansRequests'), requestData);
      
      // Send WhatsApp notification to roaster
      try {
        await sendRoasterNotification({
          ...requestData,
          id: docRef.id,
          createdAt: { toDate: () => new Date() } // Convert for notification service
        });
        console.log('Roaster notification sent successfully');
      } catch (notificationError) {
        console.error('Failed to send roaster notification:', notificationError);
        // Don't fail the whole request if notification fails
      }
      
      setSuccess(`Request submitted successfully! You requested ${quantity} units of ${selectedBean.beanName}.`);
      setCurrentStep('success');
      
      // Reset form
      setRequestQuantity('');
      setNotes('');
      setSelectedBean(null);
      
    } catch (error) {
      console.error('Error submitting request:', error);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomBeanRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const quantity = parseInt(customQuantity);
      
      if (!customBeanName.trim()) {
        setError('Please enter a bean name');
        setLoading(false);
        return;
      }

      if (quantity <= 0) {
        setError('Please enter a valid quantity');
        setLoading(false);
        return;
      }

      // Custom request data (no beanId since it's not in inventory)
      const requestData = {
        beanId: null, // Custom request - no inventory item
        beanName: customBeanName.trim(),
        unit: customUnit,
        unitSize: null, // No unit size for custom requests
        requestedQuantity: quantity,
        availableQuantity: null, // No inventory availability
        notes: notes.trim() || '',
        status: 'pending',
        requestedBy: currentUser.uid,
        requestedByName: currentUser.displayName || currentUser.email,
        requestedByRole: currentUser.role || 'Staff',
        createdAt: new Date(),
        updatedAt: new Date(),
        isCustomRequest: true
      };

      const docRef = await addDoc(collection(db, 'beansRequests'), requestData);
      
      // Send WhatsApp notification to roaster
      try {
        await sendRoasterNotification({
          ...requestData,
          id: docRef.id,
          createdAt: { toDate: () => new Date() } // Convert for notification service
        });
        console.log('Roaster notification sent successfully for custom request');
      } catch (notificationError) {
        console.error('Failed to send roaster notification:', notificationError);
        // Don't fail the whole request if notification fails
      }
      
      setSuccess(`Custom request submitted successfully! You requested ${quantity}${customUnit} of ${customBeanName}.`);
      setCurrentStep('success');
      
      // Reset form
      setCustomBeanName('');
      setCustomUnit('kg');
      setCustomQuantity('');
      setNotes('');
      
    } catch (error) {
      console.error('Error submitting custom request:', error);
      setError('Failed to submit custom request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtherOptionsClick = () => {
    setCurrentStep('custom');
  };

  const handleMilkRequestClick = () => {
    setCurrentStep('milk');
  };

  const handleMilkRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const quantity = parseFloat(milkQuantity);
      
      if (!milkType.trim()) {
        setError('Please select a milk type');
        setLoading(false);
        return;
      }

      if (quantity <= 0) {
        setError('Please enter a valid quantity');
        setLoading(false);
        return;
      }

      // Milk request data
      const requestData = {
        beanId: null, // Not a bean request
        beanName: null,
        milkType: milkType.trim(),
        unit: milkUnit,
        unitSize: null,
        requestedQuantity: quantity,
        availableQuantity: null,
        notes: notes.trim() || '',
        status: 'pending',
        requestedBy: currentUser.uid,
        requestedByName: currentUser.displayName || currentUser.email,
        requestedByRole: currentUser.role || 'Staff',
        createdAt: new Date(),
        updatedAt: new Date(),
        isMilkRequest: true
      };

      const docRef = await addDoc(collection(db, 'beansRequests'), requestData);
      
      // Send WhatsApp notification to roaster
      try {
        await sendRoasterNotification({
          ...requestData,
          id: docRef.id,
          createdAt: { toDate: () => new Date() }
        });
        console.log('Roaster notification sent successfully for milk request');
      } catch (notificationError) {
        console.error('Failed to send roaster notification:', notificationError);
      }
      
      setSuccess(`Milk request submitted successfully! You requested ${quantity} ${milkUnit} of ${milkType}.`);
      setCurrentStep('success');
      
      // Reset form
      setMilkType('');
      setMilkQuantity('');
      setMilkUnit('liters');
      setNotes('');
      
    } catch (error) {
      console.error('Error submitting milk request:', error);
      setError('Failed to submit milk request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromCustom = () => {
    setCurrentStep('select');
    setCustomBeanName('');
    setCustomUnit('kg');
    setCustomQuantity('');
    setNotes('');
    setError(null);
  };

  const handleBackFromMilk = () => {
    setCurrentStep('select');
    setMilkType('');
    setMilkQuantity('');
    setMilkUnit('liters');
    setNotes('');
    setError(null);
  };

  const resetToSelection = () => {
    setCurrentStep('select');
    setSuccess(null);
    setError(null);
    setRequestQuantity('');
    setNotes('');
    setSelectedBean(null);
    setCustomBeanName('');
    setCustomUnit('kg');
    setCustomQuantity('');
    setMilkType('');
    setMilkQuantity('');
    setMilkUnit('liters');
  };

  return (
    <div style={{
      background: '#f8f9fa',
      minHeight: '100vh',
      padding: '0',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      paddingTop: '0'
    }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        padding: '20px',
        margin: '0',
        borderBottom: '1px solid #e9ecef'
      }}>
        <h1 style={{
          fontFamily: 'Inter, Arial, sans-serif',
          fontSize: '32px',
          fontWeight: '700',
          color: '#2d3748',
          margin: '0',
          letterSpacing: '1px',
          textTransform: 'uppercase'
        }}>BEANS REQUEST</h1>
        <div style={{
          fontSize: '14px',
          color: '#6c757d',
          marginTop: '4px',
          fontFamily: 'Georgia, serif'
        }}>
          Request coffee beans from roaster inventory
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: '#fed7d7',
          border: '1px solid #feb2b2',
          color: '#c53030',
          padding: '16px 20px',
          margin: '16px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FaExclamationTriangle />
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#c53030',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: 'auto'
            }}
          >×</button>
        </div>
      )}

      {success && (
        <div style={{
          background: '#c6f6d5',
          border: '1px solid #9ae6b4',
          color: '#276749',
          padding: '16px 20px',
          margin: '16px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FaCheck />
          <span>{success}</span>
        </div>
      )}

      {/* Content */}
      <div style={{ 
        background: '#fff', 
        borderRadius: '8px', 
        marginTop: '10px',
        padding: '20px',
        minHeight: '100vh',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        {/* Step 1: Bean Selection */}
        {currentStep === 'select' && (
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#2d3748',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FaCoffee style={{ color: '#8b5a3c' }} />
              Select Coffee Beans
            </h2>
            
            {availableBeans.length === 0 ? (
              <div style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '40px 20px',
                textAlign: 'center',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <FaCoffee size={48} style={{ color: '#cbd5e0', marginBottom: '16px' }} />
                <h3 style={{
                  color: '#4a5568',
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 8px 0'
                }}>No beans available</h3>
                <p style={{
                  color: '#718096',
                  fontSize: '14px',
                  margin: '0'
                }}>Contact your roaster to add beans to the inventory</p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {availableBeans.map((bean, index) => (
                  <div key={bean.id} style={{
                    background: '#fff',
                    borderRadius: '8px',
                    padding: '20px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: '#2d3748'
                  }}
                  onClick={() => handleBeanSelect(bean)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2d3748',
                        margin: '0'
                      }}>{bean.beanName}</h3>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: bean.quantity <= 5 ? '#fff3cd' : '#f8f9fa',
                        color: bean.quantity <= 5 ? '#856404' : '#6c757d',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: bean.quantity <= 5 ? '1px solid #ffeaa7' : '1px solid #dee2e6'
                      }}>
                        <FaWeight size={10} />
                        {bean.quantity} units
                      </div>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6c757d',
                      marginBottom: '8px'
                    }}>
                      {bean.unitSize}{bean.unit} per unit
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6c757d'
                    }}>
                      Total: {(bean.quantity * bean.unitSize).toFixed(1)}{bean.unit} available
                    </div>
                  </div>
                ))}
                
                {/* Request Milk Button */}
                <button
                  onClick={handleMilkRequestClick}
                  style={{
                    background: '#fff',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2d3748',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    marginBottom: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                >
                  <FaGlassWhiskey size={16} />
                  Request Milk
                </button>

                {/* Other Options Button */}
                <button
                  onClick={handleOtherOptionsClick}
                  style={{
                    background: '#fff',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#2d3748',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }}
                >
                  <FaPlus size={16} />
                  Other Options (Custom Bean)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Request Form */}
        {currentStep === 'request' && selectedBean && (
          <div>
            <button
              onClick={handleBackToSelection}
              style={{
                background: 'none',
                border: 'none',
                color: '#4299e1',
                fontSize: '14px',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 0'
              }}
            >
              <FaArrowLeft size={12} />
              Back to Bean Selection
            </button>

            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 12px 0'
              }}>{selectedBean.beanName}</h3>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#718096' }}>Available Stock:</span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: selectedBean.quantity <= 5 ? '#e53e3e' : '#38a169'
                }}>{selectedBean.quantity} units</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#718096' }}>Unit Size:</span>
                <span style={{ fontSize: '14px', color: '#4a5568' }}>
                  {selectedBean.unitSize}{selectedBean.unit} per unit
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #f7fafc',
                paddingTop: '8px'
              }}>
                <span style={{ fontSize: '14px', color: '#718096' }}>Total Available:</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                  {(selectedBean.quantity * selectedBean.unitSize).toFixed(1)}{selectedBean.unit}
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmitRequest} style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Request Quantity (units)
                </label>
                <input 
                  type="number"
                  value={requestQuantity}
                  onChange={(e) => setRequestQuantity(e.target.value)}
                  placeholder="Enter number of units"
                  min="1"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
                <div style={{
                  fontSize: '12px',
                  color: '#718096',
                  marginTop: '4px'
                }}>
                  {requestQuantity && !isNaN(requestQuantity) && requestQuantity > 0 && (
                    <span>
                      = {(requestQuantity * selectedBean.unitSize).toFixed(1)}{selectedBean.unit} total
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Notes (Optional)
                </label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. For morning rush, Special customer order, etc."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <button 
                type="submit"
                disabled={loading || !requestQuantity || requestQuantity <= 0}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: loading || !requestQuantity || requestQuantity <= 0 ? '#f8f9fa' : '#2d3748',
                  color: loading || !requestQuantity || requestQuantity <= 0 ? '#6c757d' : '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading || !requestQuantity || requestQuantity <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: loading || !requestQuantity || requestQuantity <= 0 ? 'none' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #fff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaClipboardList />
                    Submit Request
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Custom Bean Request */}
        {currentStep === 'custom' && (
          <div>
            <button
              onClick={handleBackFromCustom}
              style={{
                background: 'none',
                border: 'none',
                color: '#4299e1',
                fontSize: '14px',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 0'
              }}
            >
              <FaArrowLeft size={12} />
              Back to Bean Selection
            </button>

            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaPlus style={{ color: '#8b5a3c' }} />
                Request Custom Bean
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                margin: '0',
                lineHeight: '1.5'
              }}>
                Request a bean that's not currently in the inventory. The roaster will review your request and decide whether to approve it.
              </p>
            </div>

            <form onSubmit={handleCustomBeanRequest} style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Bean Name
                </label>
                <input 
                  type="text"
                  value={customBeanName}
                  onChange={(e) => setCustomBeanName(e.target.value)}
                  placeholder="e.g. Ethiopian Yirgacheffe, Colombian Supremo"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Unit
                </label>
                <select 
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: '#fff',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="kg">Kilograms (kg)</option>
                  <option value="g">Grams (g)</option>
                  <option value="pack">Packs</option>
                  <option value="bags">Bags</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Quantity
                </label>
                <input 
                  type="number"
                  value={customQuantity}
                  onChange={(e) => setCustomQuantity(e.target.value)}
                  placeholder="Enter quantity needed"
                  min="1"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Notes (Optional)
                </label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Specific roast profile, origin details, customer preference, etc."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <button 
                type="submit"
                disabled={loading || !customBeanName.trim() || !customQuantity || customQuantity <= 0}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: loading || !customBeanName.trim() || !customQuantity || customQuantity <= 0 ? '#f8f9fa' : '#2d3748',
                  color: loading || !customBeanName.trim() || !customQuantity || customQuantity <= 0 ? '#6c757d' : '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading || !customBeanName.trim() || !customQuantity || customQuantity <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: loading || !customBeanName.trim() || !customQuantity || customQuantity <= 0 ? 'none' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #fff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaClipboardList />
                    Submit Custom Request
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 4: Milk Request */}
        {currentStep === 'milk' && (
          <div>
            <button
              onClick={handleBackFromMilk}
              style={{
                background: 'none',
                border: 'none',
                color: '#4299e1',
                fontSize: '14px',
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 0'
              }}
            >
              <FaArrowLeft size={12} />
              Back to Selection
            </button>

            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#2d3748',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaGlassWhiskey style={{ color: '#8b5a3c' }} />
                Request Milk
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                margin: '0',
                lineHeight: '1.5'
              }}>
                Request milk for your coffee operations. Specify the type and quantity needed.
              </p>
            </div>

            <form onSubmit={handleMilkRequest} style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Milk Type
                </label>
                <select 
                  value={milkType}
                  onChange={(e) => setMilkType(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: '#fff',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select milk type</option>
                  <option value="Whole Milk">Whole Milk</option>
                  <option value="2% Milk">2% Milk</option>
                  <option value="Skim Milk">Skim Milk</option>
                  <option value="Oat Milk">Oat Milk</option>
                  <option value="Almond Milk">Almond Milk</option>
                  <option value="Soy Milk">Soy Milk</option>
                  <option value="Coconut Milk">Coconut Milk</option>
                  <option value="Lactose-Free Milk">Lactose-Free Milk</option>
                  <option value="Heavy Cream">Heavy Cream</option>
                  <option value="Half & Half">Half & Half</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Unit
                </label>
                <select 
                  value={milkUnit}
                  onChange={(e) => setMilkUnit(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    background: '#fff',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="liters">Liters</option>
                  <option value="gallons">Gallons</option>
                  <option value="cartons">Cartons</option>
                  <option value="bottles">Bottles</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Quantity
                </label>
                <input 
                  type="number"
                  step="0.1"
                  value={milkQuantity}
                  onChange={(e) => setMilkQuantity(e.target.value)}
                  placeholder="Enter quantity needed"
                  min="0.1"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '8px'
                }}>
                  Notes (Optional)
                </label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. For latte art, Special dietary requirements, etc."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6c757d';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                  }}
                />
              </div>

              <button 
                type="submit"
                disabled={loading || !milkType || !milkQuantity || milkQuantity <= 0}
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  background: loading || !milkType || !milkQuantity || milkQuantity <= 0 ? '#f8f9fa' : '#2d3748',
                  color: loading || !milkType || !milkQuantity || milkQuantity <= 0 ? '#6c757d' : '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading || !milkType || !milkQuantity || milkQuantity <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: loading || !milkType || !milkQuantity || milkQuantity <= 0 ? 'none' : '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #fff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaGlassWhiskey />
                    Submit Milk Request
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 5: Success */}
        {currentStep === 'success' && (
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '40px 20px',
            border: '1px solid #e9ecef',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#28a745',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <FaCheck size={24} style={{ color: '#fff' }} />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#2d3748',
              margin: '0 0 8px 0'
            }}>Request Submitted!</h2>
            <p style={{
              fontSize: '16px',
              color: '#718096',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              Your beans request has been submitted successfully. 
              The roaster will review and process your request soon.
            </p>
            {success && (
              <div style={{
                background: '#f7fafc',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px',
                fontSize: '14px',
                color: '#4a5568'
              }}>
                {success}
              </div>
            )}
            <button
              onClick={resetToSelection}
              style={{
                background: '#2d3748',
                color: '#fff',
                border: '1px solid #e9ecef',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              }}
            >
              Submit Another Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BaristaBeansRequestPage;